import React, { useEffect, useState, useCallback } from 'react';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import { shopApi } from '../../../utils/shopApi';
import { useAuth } from '../../../contexts/AuthContext';
import { formatDate } from '../../../utils/dates';
import {
  PageHeader, Tabs, useUrlTab, Sheet, Skeleton, EmptyState, ErrorState, useConfirm, useToast,
} from '../../../components/ui';
import '../booking-shared.css';
import './AdminShopOrders.css';

const ORDER_STATUS = {
  ORDERED: { label: 'In progress', tone: 'wait' },
  ARRIVED: { label: 'Arrived at club', tone: 'info' },
  FULFILLED: { label: 'Collected', tone: 'good' },
  ARCHIVED: { label: 'Archived', tone: 'off' },
};

const ITEM_STATUS = {
  AWAITING: { label: 'Awaiting', tone: 'bad' },
  ORDERED_FROM_SUPPLIER: { label: 'Ordered from supplier', tone: 'wait' },
  ARRIVED: { label: 'Arrived at club', tone: 'info' },
  FULFILLED: { label: 'Collected', tone: 'good' },
};

const ITEM_NEXT = {
  AWAITING: 'ORDERED_FROM_SUPPLIER',
  ORDERED_FROM_SUPPLIER: 'ARRIVED',
  ARRIVED: 'FULFILLED',
};

const ITEM_NEXT_LABEL = {
  AWAITING: 'Mark ordered',
  ORDERED_FROM_SUPPLIER: 'Mark arrived',
  ARRIVED: 'Mark collected',
};

const ITEM_STATUSES = ['AWAITING', 'ORDERED_FROM_SUPPLIER', 'ARRIVED', 'FULFILLED'];

// The tabs, in the order they show; In progress opens first
const TABS = [
  { id: 'ALL', label: 'All' },
  { id: 'ORDERED', label: 'In progress' },
  { id: 'ARRIVED', label: 'Arrived at club' },
  { id: 'FULFILLED', label: 'Collected' },
  { id: 'ARCHIVED', label: 'Archived' },
];

const Pill = ({ status, from }) => (
  <span className={`bk-pill bk-pill--${from[status]?.tone || 'off'}`}>{from[status]?.label || status}</span>
);

// Correcting an item: who supplies it and where it's got to
function EditItemSheet({ item, onClose, onSave }) {
  const [supplier, setSupplier] = useState(item.supplier ?? '');
  const [status, setStatus] = useState(item.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const changes = {};
  if (supplier.trim() !== (item.supplier ?? '')) changes.supplier = supplier.trim();
  if (status !== item.status) changes.status = status;
  const changed = Object.keys(changes).length > 0;

  const submit = async () => {
    if (!changed) { onClose(); return; }
    setBusy(true);
    setError(null);
    const problem = await onSave(changes);
    if (problem) { setError(problem); setBusy(false); }
  };

  return (
    <Sheet
      title={`${item.productName}, ${item.size}`}
      onClose={onClose}
      dirty={changed}
      onSubmit={submit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <div className="so-form">
        <label className="bk-label">
          Supplier<span className="bk-optional"> (optional)</span>
          <input className="bk-input" value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="For example, Champion" />
        </label>
        <label className="bk-label">
          Status
          <select className="bk-input" value={status} onChange={e => setStatus(e.target.value)}>
            {ITEM_STATUSES.map(s => <option key={s} value={s}>{ITEM_STATUS[s].label}</option>)}
          </select>
        </label>
        {error && <p className="bk-form-error" role="alert">{error}</p>}
      </div>
    </Sheet>
  );
}

// readOnly: View access to the shop; changes need Manage
function ItemRow({ item, busy, onAdvance, onEdit, readOnly = false }) {
  const next = ITEM_NEXT[item.status];
  return (
    <li className="so-item">
      <div className="so-item__top">
        <div className="so-item__what">
          <div className="so-item__name">{item.productName}, {item.size}</div>
          <div className="bk-small">
            {item.customisation ? `${item.customisation} · ` : ''}qty {item.quantity}
            {' · '}Supplier: {item.supplier || <span className="bk-muted">Not set</span>}
          </div>
        </div>
        <Pill status={item.status} from={ITEM_STATUS} />
      </div>
      {!readOnly && (
        <div className="bk-row-actions">
          {next && (
            <button type="button" className="bk-btn bk-btn--sm bk-btn--primary" disabled={busy} onClick={onAdvance}>
              {busy ? 'Saving…' : ITEM_NEXT_LABEL[item.status]}
            </button>
          )}
          <button type="button" className="bk-btn bk-btn--sm" disabled={busy} onClick={onEdit} aria-label={`Edit ${item.productName}, ${item.size}`}>
            Edit
          </button>
        </div>
      )}
    </li>
  );
}

export default function AdminShopOrders() {
  const { can } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const managesShop = can('shop', 'MANAGE');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useUrlTab(['ORDERED', 'ALL', 'ARRIVED', 'FULFILLED', 'ARCHIVED']);
  const [busyItem, setBusyItem] = useState(null);
  const [busyOrder, setBusyOrder] = useState(null);
  const [editing, setEditing] = useState(null); // { orderId, item }
  const [error, setError] = useState(null);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    shopApi.getAdminOrders(activeTab)
      .then(res => setOrders(res.data))
      .catch((err) => { console.error('Could not load orders', err); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [activeTab]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Gives back a problem to show, or nothing when it worked
  const updateItem = async (orderId, itemId, payload) => {
    try {
      const res = await shopApi.updateOrderItem(orderId, itemId, payload);
      setOrders(prev => prev.map(o => (o.id === orderId ? res.data : o)));
      return null;
    } catch (err) {
      return err.response?.data?.error || "Couldn't update the item. Try again.";
    }
  };

  const advance = async (orderId, item) => {
    setBusyItem(item.id);
    setError(null);
    const problem = await updateItem(orderId, item.id, { status: ITEM_NEXT[item.status] });
    if (problem) setError(problem);
    setBusyItem(null);
  };

  const saveEdit = async (changes) => {
    const problem = await updateItem(editing.orderId, editing.item.id, changes);
    if (!problem) {
      toast(`${editing.item.productName} saved`);
      setEditing(null);
    }
    return problem;
  };

  const archiveOrRestore = async (order) => {
    let note;
    if (!order.archivedAt) {
      note = await confirm({
        title: 'Archive this order?',
        message: 'For an order that was abandoned or never collected. It moves to the Archived tab and you can restore it.',
        confirmLabel: 'Archive',
        tone: 'primary',
        input: { label: 'Note', placeholder: 'For example, never collected' },
      });
      if (note === false) return;
      // Left blank, it says why, as the old box filled in for you
      note = note || 'Never collected';
    }
    setBusyOrder(order.id);
    setError(null);
    try {
      await (order.archivedAt ? shopApi.restoreOrder(order.id) : shopApi.archiveOrder(order.id, note));
      setOrders(prev => prev.filter(o => o.id !== order.id));
      toast(order.archivedAt ? 'Order restored' : 'Order archived');
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't update the order. Try again.");
    } finally {
      setBusyOrder(null);
    }
  };

  return (
    <div className="bk-page bk-page--wide">
      <PageHeader back={{ to: '/admin-hub', label: 'Admin' }} title="Shop orders" />

      <Tabs tabs={TABS} value={activeTab} onChange={setActiveTab} label="Order status" />

      {error && <p className="bk-form-error" role="alert">{error}</p>}
      {loadError && <ErrorState title="Couldn't load orders" onRetry={fetchOrders} />}
      {!loadError && loading && <Skeleton rows={3} label="Loading orders…" />}
      {!loadError && !loading && orders.length === 0 && (
        <EmptyState icon={ShoppingBagIcon} title="No orders here" text="Orders from the kit shop show here once they're paid." />
      )}

      {!loadError && !loading && orders.map(order => (
        <div key={order.id} className="bk-card so-order">
          <div className="so-order__head">
            <div className="so-order__who">
              <strong>{order.user ? `${order.user.firstName} ${order.user.lastName}` : <span className="bk-muted">Not set</span>}</strong>
              {order.user?.email && <span className="bk-small">{order.user.email}</span>}
            </div>
            <div className="so-order__tags">
              <Pill status={order.status} from={ORDER_STATUS} />
              {order.archivedAt && order.status !== 'ARCHIVED' && <Pill status="ARCHIVED" from={ORDER_STATUS} />}
            </div>
          </div>
          <p className="bk-small so-order__date">Ordered {formatDate(order.createdAt)}</p>

          <ul className="so-items">
            {order.items.map(item => (
              <ItemRow
                key={item.id}
                item={item}
                busy={busyItem === item.id}
                onAdvance={() => advance(order.id, item)}
                onEdit={() => setEditing({ orderId: order.id, item })}
                readOnly={!managesShop}
              />
            ))}
          </ul>

          <p className="so-order__total">Total: £{(order.total / 100).toFixed(2)}</p>
          {order.archivedAt && (
            <p className="bk-small">
              Archived {formatDate(order.archivedAt)}{order.archivedNote ? `: ${order.archivedNote}` : ''}
            </p>
          )}
          <p className="bk-small">Ref: {order.id}</p>
          {managesShop && (order.archivedAt || order.status !== 'FULFILLED') && (
            <div className="bk-actions">
              <button
                type="button"
                className={order.archivedAt ? 'bk-btn' : 'bk-btn bk-btn--outline-danger'}
                disabled={busyOrder === order.id}
                onClick={() => archiveOrRestore(order)}
              >
                {order.archivedAt ? 'Restore order' : 'Archive order'}
              </button>
            </div>
          )}
        </div>
      ))}

      {editing && (
        <EditItemSheet item={editing.item} onClose={() => setEditing(null)} onSave={saveEdit} />
      )}
    </div>
  );
}
